import React, {
    createRef,
    useContext,
    useState,
    useEffect,
    useCallback,
} from 'react';
import {Route, Redirect, withRouter} from 'react-router-dom';
import {Container, Button, Hidden, MobileStepper, Paper, Grid} from '@material-ui/core';
import {withStyles} from '@material-ui/core/styles';
import {LocalizationProvider} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import useBrowserTitle from 'hooks/useBrowserTitle';
import {snakeCaseToCapitalCase} from 'components/forms/locale/utils';
import {FormNavigation} from 'components/forms/context';
import {BranchGroup} from 'components/forms/navigation';
import {Loop, withNavigation} from 'components/forms/navigation';
import {withStateMachine} from 'components/forms/FormStateProvider';
import {FormState} from 'components/forms/context';
import {useFormPersistence} from 'components/forms/hooks';
import {LoopMetadata} from 'components/forms/context/LoopMeta';
import {findBranch} from 'components/forms/utils/branch';
import {AppCtx} from 'context/AppContext';
import DocumentViewer from './DocumentViewer';
import useWidth from 'hooks/useWidth';
import useScroll from 'hooks/useScroll';
import get from 'lodash.get';
import ErrorBoundary from 'components/utils/ErrorBoundary';

const styles = {
    root: {
        paddingTop: '.5em',
        paddingBottom: '1em',
    },
};

const splitWidths = ['lg', 'xl'];

const ProgressForm = ({
    match,
    history,
    classes,
    title,
    children,
    node,
    addForm = true,
    stretchPaper = false,
}) => {
    // Get container ref
    const containerRef = createRef();
    const form = createRef();
    const width = useWidth();
    const {position} = useScroll();
    const splitWidth = ~splitWidths.indexOf(width);

    // Current form state
    const {state, updateState} = useFormPersistence(node);

    // Looping Metadata
    const {data: metaData, clearData} = useContext(LoopMetadata);
    // LoopNavigators
    const [loopNavigators, setLoopNavs] = useState({});
    const setLoopNavigators = (name, navigator) => {
        setLoopNavs(prevNavigators => ({
            ...prevNavigators,
            [name]: navigator,
        }));
    };
    // Navigation Bookmarks
    const [navigationBookmarks, setNavigationBookmarks] = useState([]);
    const setNavigationBookmark = bookmark => {
        setNavigationBookmarks(prevBookmarks => [
            ...prevBookmarks,
            ...bookmark,
        ]);
    };

    // Reset Loop data on unmount
    useEffect(() => {
        return () => {
            clearData();
        };
        // We only want to run this on unmount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Set global context to show we're in a form
    const {setInForm} = useContext(AppCtx);
    useEffect(() => {
        setInForm(true);
        return () => {
            setInForm(false);
        };
    }, [setInForm]);

    // User Feedback ----------------------------------------------------------
    // Update browser title with form section
    useBrowserTitle(
        `| ${title} | ${snakeCaseToCapitalCase(match.params.formStep)}`
    );

    // Server Response --------------------------------------------------------
    // Server Form State Data
    const [response, setResponse] = useState(null);

    // Navigation -------------------------------------------------------------
    // Navigation pushed up from sections
    const {back, next} = useContext(FormNavigation);

    // loop index context in this component?
    const getSections = useCallback(
        (chilluns, stateOverride) => {
            let decisionState = {};
            if (stateOverride) {
                decisionState = {...state, ...stateOverride};
            } else {
                decisionState = state;
            }

            return React.Children.toArray(chilluns).reduce((kids, child) => {
                if (child.type === BranchGroup) {
                    const validBranch = React.Children.toArray(
                        child.props.children
                    ).find(branch =>
                        findBranch(branch, metaData, decisionState)
                    );
                    return [
                        ...kids,
                        ...getSections(
                            validBranch?.props.children,
                            stateOverride
                        ),
                    ];
                }
                if (child.type === Loop) {
                    return [
                        ...kids,
                        ...getSections(child.props.children, stateOverride),
                    ];
                }
                if (child.type === Route) {
                    return [...kids, child];
                }

                return kids;
            }, []);
        },
        [metaData, state]
    );

    const sections = getSections(children);
    const currentSection = sections.find(
        section => section.props.path === match.url
    );

    /**
     * Navigate according to the passed direction/step number
     * @param {number} direction 1 or -1 to denote next/previous
     */
    const navigate = useCallback(
        (direction, updatedState) => {
            // calculate new sections based on updated state
            const updatedTree = getSections(children, updatedState);
            const bookmarkIndex = navigationBookmarks.findIndex(
                thisBookmark => {
                    return thisBookmark.path === window.location.pathname;
                }
            );
            const bookmark = navigationBookmarks[bookmarkIndex];
            let nextSection;
            let bookmarkPath;

            if (direction === 'review') {
                nextSection = updatedTree.find(item =>
                    item.props.path.endsWith('review')
                );
            } else {
                // If we matched a bookmark, check the direction
                // if matched, set the path
                if (bookmark) {
                    if (bookmark.direction === direction) {
                        bookmarkPath = bookmark.origin;
                    }
                }
                if (!bookmarkPath) {
                    nextSection =
                        updatedTree[
                            updatedTree.findIndex(
                                item =>
                                    item?.props?.path ===
                                    currentSection?.props?.path
                            ) + direction
                        ];
                }
            }

            // Navigate to bookmark, reset bookmarks with used one removed
            if (bookmarkPath) {
                // Remove any bookmarks set by the page we're going to
                // in addition to the current bookmark
                const otherBookmarks = navigationBookmarks.filter(
                    (bookmk, index) => index !== bookmarkIndex
                );

                history.push(bookmarkPath);
                updateState({
                    ...state,
                    inReview: false,
                });
                setNavigationBookmarks([...otherBookmarks]);
            } else if (nextSection) {
                const otherBookmarks = navigationBookmarks.filter(
                    bookmk => bookmk.origin !== nextSection.props.path
                );

                if (navigationBookmarks.length !== otherBookmarks.length) {
                    updateState({
                        ...state,
                        inReview: false,
                    });
                }

                history.push(nextSection.props.path);
                setNavigationBookmarks([...otherBookmarks]);
            } else {
                console.error(
                    'Could not determine which section to navigate to, this probably indicates that branching is operating with old state'
                );
            }
        },
        [
            children,
            currentSection?.props.path,
            getSections,
            history,
            navigationBookmarks,
            state,
            updateState,
        ]
    );

    const handleBackClick = () => {
        let proceed = true;
        if (typeof back?.beforeOnClick === 'function') {
            proceed = back.beforeOnClick();
        }
        if (proceed) {
            const updatedState = back?.onClick();
            updatedState && navigate(-1, updatedState);
        }
    };
    const handleNextClick = useCallback(async () => {
        let proceed = true;
        if (typeof next?.beforeOnClick === 'function') {
            proceed = await next.beforeOnClick();
        }
        if (proceed) {
            const updatedState = await next?.onClick();
            updatedState && navigate(1, updatedState);
        }
    }, [next, navigate]);

    const showDocumentViewer =
        !currentSection?.props?.path?.match(/\/(upload|review)/) &&
        get(state, 'user_document_post_response.user_images', [])?.length;

    const navigateToNamedLoop = (name, index, parentName, parentIndex) => {
        // Pop to named loop's start
        const contextLoop = loopNavigators[name];

        updateState({
            ...state,
            inReview: true,
        });
        // Ensure we're setting the higher level context also
        if (parentName) {
            loopNavigators[parentName].setIndex(parentIndex);
        }
        contextLoop.setIndex(index, parentName, parentIndex);
        contextLoop.goTo();
    };

    const deleteNamedLoop = (name, index, parentName, parentIndex) => {
        // Pop to named loop's start
        const contextLoop = loopNavigators[name];
        contextLoop.deleteChild(index, parentName, parentIndex);
    };

    const saveReturnReview = async () => {
        let proceed = true;
        if (typeof next?.beforeOnClick === 'function') {
            proceed = await next.beforeOnClick();
        }
        updateState({
            ...state,
            inReview: false,
        });
        if (proceed) {
            const updatedState = await next?.onClick();
            updatedState && navigate('review', updatedState);
        }
    };

    useEffect(() => {
        const navOnSubmit = () => {
            handleNextClick();
        };
        const formRef = form.current;
        formRef?.addEventListener('submit', navOnSubmit);

        return () => {
            formRef?.removeEventListener('submit', navOnSubmit);
        };
    }, [handleNextClick, form]);

    useEffect(() => {
        window.dataLayer.push({
            event: 'form-sesion-start',
            data: {
                formType: node,
            },
        });
        return () => {
            window.dataLayer.push({
                event: 'form-session-end',
                data: {
                    formType: node,
                },
            });
        };
    }, [node]);

    return (
        <ErrorBoundary>
            <FormState.Provider
                value={{
                    node,
                    response,
                    setResponse,
                    navigate,
                    navigateToNamedLoop,
                    deleteNamedLoop,
                    setLoopNavigators,
                    setNavigationBookmark,
                }}
            >
                <LocalizationProvider dateAdapter={MomentUtils}>
                    <Grid
                        container
                        justifyContent='space-evenly'
                        alignItems='stretch'
                        ref={form}
                        spacing={2}
                    >
                        <Grid
                            item
                            xs={stretchPaper ? true : 12}
                            lg={
                                stretchPaper
                                    ? true
                                    : showDocumentViewer
                                    ? 6
                                    : 12
                            }
                        >
                            <Paper elevation={2}>
                                <Container classes={classes}>
                                    <h1>
                                        {addForm && 'Add Form'} {title}
                                    </h1>
                                    <Route path={`/forms/${node}/start`}>
                                        <Redirect
                                            to={`${sections[0].props.path}`}
                                        />
                                    </Route>
                                    {children}
                                    {state.inReview && (
                                        <Button
                                            fullWidth
                                            color='primary'
                                            variant='outlined'
                                            onClick={saveReturnReview}
                                            style={{
                                                marginTop: '.5em',
                                            }}
                                        >
                                            Save and return to review
                                        </Button>
                                    )}
                                </Container>
                                <MobileStepper
                                    variant='progress'
                                    steps={sections.length}
                                    position='static'
                                    activeStep={sections.indexOf(
                                        currentSection
                                    )}
                                    nextButton={
                                        <Button
                                            onClick={handleNextClick}
                                            disabled={next?.disabled}
                                            data-cy={next?.testHook}
                                            color={next?.color}
                                            variant='contained'
                                            disableElevation
                                            title={next?.label}
                                        >
                                            <Hidden xsDown>
                                                {next?.label}
                                            </Hidden>
                                            {React.createElement(next?.icon)}
                                        </Button>
                                    }
                                    backButton={
                                        <Button
                                            onClick={handleBackClick}
                                            disabled={back?.disabled}
                                            data-cy={back?.testHook}
                                            color={back?.color}
                                            title={back?.label}
                                        >
                                            {React.createElement(back?.icon)}
                                            <Hidden xsDown>
                                                {back?.label}
                                            </Hidden>
                                        </Button>
                                    }
                                />
                            </Paper>
                        </Grid>
                        {showDocumentViewer ? (
                            <Grid
                                container
                                item
                                direction='column'
                                ref={containerRef}
                                xs={12}
                                lg={
                                    stretchPaper
                                        ? true
                                        : showDocumentViewer
                                        ? 6
                                        : 12
                                }
                            >
                                <DocumentViewer
                                    assets={
                                        state?.user_document_post_response
                                            ?.user_images
                                    }
                                    ref={containerRef}
                                    style={{
                                        '--scroll-y': `${
                                            splitWidth ? position?.y || 0 : 0
                                        }px`,
                                        transition:
                                            'transform 100ms ease-in-out',
                                        transform: `translateY(var(--scroll-y))`,
                                    }}
                                />
                            </Grid>
                        ) : null}
                    </Grid>
                </LocalizationProvider>
            </FormState.Provider>
        </ErrorBoundary>
    );
};
export default withStyles(styles)(
    withRouter(withStateMachine(withNavigation(ProgressForm)))
);
